import React from 'react';
import { Link } from 'react-router-dom';
import { I18n } from 'react-redux-i18n';

import { StatusBadge } from '../StatusBadge/StatusBadge';
import { WriteOnly } from '../grid/WriteOnly';
import type { TicketingSalesData } from '../../../pages/entity/performance/types';
import styles from './TicketingSalesWidget.module.css';

export interface TicketingSalesWidgetProps {
  data?: TicketingSalesData | null;
  /** Check-in page link, only passed while check-in is open. */
  checkInUrl?: string;
}

const formatCount = (value: number) =>
  (Number(value) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const hasBreakdown = (data: TicketingSalesData) =>
  data.ticketTypes.length > 0 || data.merchandise.length > 0;

/**
 * TicketingSalesWidget - lifetime ("to date") ticketing snapshot for the
 * campaign performance page. Shows the campaign-wide ticket cap progress, how many
 * ticket holders checked in, and a per-ticket-type and per-merchandise sold breakdown. Like the gift-matching
 * widget, it is independent of the page's time-period selector.
 */
export const TicketingSalesWidget: React.FC<TicketingSalesWidgetProps> = ({
  data,
  checkInUrl
}) => {
  if (!data || !data.enabled) return null;

  const cap = data.ticketCap?.cap ?? null;
  const sold = data.ticketCap?.sold ?? 0;
  const remaining = data.ticketCap?.remaining ?? null;
  const soldOut = data.ticketCap?.soldOut ?? false;
  const hasCap = cap != null;
  const checkedIn = data.checkedIn ?? null;
  const ticketHolders = data.ticketsSold ?? null;
  // Needs both figures, otherwise the summary would read "X of 0"
  const hasCheckIn = checkedIn != null && ticketHolders != null;

  // Nothing meaningful to show.
  if (!hasCap && !hasBreakdown(data) && !hasCheckIn) return null;

  const percentage = hasCap && cap > 0 ? Math.min((sold / cap) * 100, 100) : 0;
  const checkedInPercentage =
    hasCheckIn && ticketHolders > 0
      ? Math.min((checkedIn / ticketHolders) * 100, 100)
      : 0;

  const renderBreakdown = (
    titleKey: string,
    items: TicketingSalesData['ticketTypes'],
    total?: number | null
  ) => {
    if (items.length === 0) return null;
    return (
      <div className={styles.breakdown}>
        <div className={styles.breakdownHeader}>
          <span className={styles.breakdownTitle}>{I18n.t(titleKey)}</span>
          {total != null && (
            <span className={styles.breakdownTotal} data-testid="breakdown-total">
              {formatCount(total)}
            </span>
          )}
        </div>
        {items.map((item, index) => (
          <div className={styles.breakdownRow} key={`${item.name}-${index}`}>
            <span className={styles.breakdownName}>{item.name}</span>
            <span className={styles.breakdownValue}>{formatCount(item.sold)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.widget}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {I18n.t('campaign.performance.ticketing-sales.title')}
        </h3>
        {soldOut && (
          <StatusBadge
            label={I18n.t('campaign.performance.ticketing-sales.sold-out')}
            colorVariant="error"
          />
        )}
      </div>

      {hasCap && (
        <div className={styles.capSection}>
          <div className={styles.capRow}>
            <span className={styles.capPrimary}>{formatCount(sold)}</span>
            <span className={styles.capTarget}>
              {I18n.t('campaign.performance.ticketing-sales.of-cap', {
                cap: formatCount(cap)
              })}
            </span>
          </div>
          <div className={styles.progressBar}>
            <div
              className={`${styles.progressFill} ${soldOut ? styles.progressFillSoldOut : ''}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <div className={styles.progressMeta}>
            <span>
              {I18n.t('campaign.performance.ticketing-sales.percent', {
                percent: Math.round(percentage)
              })}
            </span>
            {!soldOut && remaining != null && (
              <span>
                {I18n.t('campaign.performance.ticketing-sales.remaining', {
                  remaining: formatCount(remaining)
                })}
              </span>
            )}
          </div>
        </div>
      )}

      {hasCheckIn && (
        <div className={styles.capSection} data-testid="check-in-summary">
          <div className={styles.breakdownHeader}>
            <span className={styles.breakdownTitle}>
              {I18n.t('campaign.performance.ticketing-sales.checked-in')}
            </span>
          </div>
          <div className={styles.capRow}>
            <span className={styles.capPrimary}>{formatCount(checkedIn)}</span>
            <span className={styles.capTarget}>
              {I18n.t('campaign.performance.ticketing-sales.checked-in-of', {
                total: formatCount(ticketHolders)
              })}
            </span>
          </div>
          <div className={styles.progressBar} aria-hidden="true">
            <div
              className={styles.progressFill}
              style={{ width: `${checkedInPercentage}%` }}
            />
          </div>
          {checkInUrl && (
            <WriteOnly>
              <Link to={checkInUrl} className={styles.checkInButton}>
                {I18n.t('campaign.performance.ticketing-sales.open-check-in')}
              </Link>
            </WriteOnly>
          )}
        </div>
      )}

      {hasBreakdown(data) && (
        <>
          {renderBreakdown(
            'campaign.performance.ticketing-sales.by-ticket-type',
            data.ticketTypes,
            data.ticketsSold
          )}
          {renderBreakdown(
            'campaign.performance.ticketing-sales.by-merchandise',
            data.merchandise,
            data.merchandiseSold
          )}
        </>
      )}
    </div>
  );
};

export default TicketingSalesWidget;
